import React, { createContext, useCallback, useContext, useState } from 'react';
import MaterialTable from 'material-table';
import { Box, IconButton, TablePagination, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

const PaginationContext = createContext({});
const ActionsContext = createContext();

function UnknownPaginationActions({ onChangePage, onPageChange, page }) {
  const { metadata, icons } = useContext(ActionsContext);
  const { t } = useTranslation();
  const from = metadata.rows ? metadata.page * metadata.pageSize + 1 : 0;
  const to = metadata.rows ? metadata.page * metadata.pageSize + metadata.rows : 0;
  const FirstIcon = icons.FirstPage;
  const PreviousIcon = icons.PreviousPage;
  const NextIcon = icons.NextPage;

  return (
    <Box display="flex" alignItems="center">
      <IconButton
        aria-label={t('pagination.first')}
        disabled={page === 0}
        onClick={(event) => (onPageChange || onChangePage)(event, 0)}
      >
        <FirstIcon />
      </IconButton>
      <IconButton
        aria-label={t('pagination.previous')}
        disabled={page === 0}
        onClick={(event) => (onPageChange || onChangePage)(event, page - 1)}
      >
        <PreviousIcon />
      </IconButton>
      <Typography variant="caption">{t('pagination.unknownRange', { from, to })}</Typography>
      <IconButton
        aria-label={t('pagination.next')}
        disabled={!metadata.next}
        onClick={(event) => (onPageChange || onChangePage)(event, page + 1)}
      >
        <NextIcon />
      </IconButton>
    </Box>
  );
}

function AdaptivePagination(paginationProps) {
  const { metadata, Pagination: customPagination } = useContext(PaginationContext);
  const Pagination = customPagination || TablePagination;
  if (
    !metadata ||
    metadata.count !== null ||
    metadata.page !== paginationProps.page ||
    metadata.pageSize !== paginationProps.rowsPerPage
  ) {
    return <Pagination {...paginationProps} />;
  }
  return (
    <ActionsContext.Provider value={{ metadata, icons: paginationProps.icons }}>
      <Pagination
        {...paginationProps}
        count={-1}
        labelDisplayedRows={() => null}
        ActionsComponent={UnknownPaginationActions}
      />
    </ActionsContext.Provider>
  );
}

export default function AdaptiveMaterialTable({ data, components, ...props }) {
  const [metadata, setMetadata] = useState();
  const loadData = useCallback(
    async (query) => {
      const result = await data(query);
      if (!result.pagination) {
        setMetadata(undefined);
        return result;
      }
      const { count, count_is_exact, next } = result.pagination;
      const exactCount = count_is_exact === false ? null : count;
      setMetadata({
        page: result.page,
        pageSize: query.pageSize,
        rows: result.data.length,
        count: exactCount,
        next,
      });
      // Numeric bounds are internal to MaterialTable; the footer never displays them as totals.
      return {
        ...result,
        totalCount:
          exactCount ??
          result.page * query.pageSize + Math.max(1, result.data.length + (next ? 1 : 0)),
      };
    },
    [data]
  );
  return (
    <PaginationContext.Provider value={{ metadata, Pagination: components?.Pagination }}>
      <MaterialTable
        {...props}
        data={typeof data === 'function' ? loadData : data}
        components={{ ...components, Pagination: AdaptivePagination }}
      />
    </PaginationContext.Provider>
  );
}
